import React, { useEffect, useMemo, useRef, useState } from "react";
import { StatusOptions } from "@/utils/constants/constants";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import EnvelopeTable from "@/components/tables/EnvelopeTable";
import TableHead from "@/components/tables/TableHead";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import { fetchFormEnvos } from "@/services/form.service";
import { transferBulkEnvelopeOwnership } from "@/services/envelope.service";
import {
    getListCacheBootstrap,
    invalidateListCacheByPage,
    invalidateOwnershipTransferListCaches,
    writeListCache,
} from "@/utils/listPageCache";
import {
    buildTransferOwnershipPayload,
    useOwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import { LABELS } from "@/utils/constants/appConstants/commonConstants";
import {
    BULK_TRANSFER_OWNERSHIP_QUEUED,
    BULK_TRANSFER_OWNERSHIP_SUCCESS,
} from "@/utils/constants/messages";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";

interface FormEnvelopeProps {
    id: string;
}

const isEmptyFilter = (value: Record<string, unknown>) =>
    Object.keys(value).length === 0;

const FormEnvelopes = (props: FormEnvelopeProps) => {
    const [
        pageNo,
        setPageNo,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();
    const isCompactLayout = useCompactListLayout();
    const selection = useOwnershipSelection();
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
    const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
    const showTransferActions = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalTransferEnvelopeOwnership);

    const [appliedFilter, setAppliedFilter] = useState(filter);

    const listBootstrap = getListCacheBootstrap(`form-${props.id}-envelopes`, {
        dataLength,
        sorting,
        searchItem,
        filter: appliedFilter,
    });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [envelopes, setEnvelopes] = useState<any[]>(listBootstrap.items);
    const [envelopesLeft, setEnvelopesLeft] = useState(true);
    const [envelopesHistory, setEnvelopesHistory] = useState<any[]>(
        listBootstrap.items
    );
    const [allFetched, setAllFetched] = useState(false);
    const [loadData, setLoadData] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);
    const loadMoreLockRef = useRef(false);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap(
            `form-${props.id}-envelopes`,
            {
                dataLength,
                sorting,
                searchItem,
                filter: appliedFilter,
            }
        );

        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        loadMoreLockRef.current = false;

        if (cached && isEmptyFilter(appliedFilter)) {
            const cachedItems = cached.items as any[];
            const hasMore = cachedItems.length < cached.totalCount;
            setEnvelopes(cachedItems);
            setEnvelopesHistory(cachedItems);
            setTotalCount(cached.totalCount);
            setEnvelopesLeft(hasMore);
            setAllFetched(!hasMore);
            setLoadData(false);
            setIsLoading(false);
            return;
        }

        setEnvelopes([]);
        setEnvelopesHistory([]);
        setIsLoading(true);
        setLoadData(true);

        fetchFormEnvos(
            props.id,
            1,
            dataLength,
            sorting,
            appliedFilter,
            searchItem,
            (response: any) => {
                const nextEnvelopes = response.data.envelopes ?? [];
                const reportedTotal = response.data.total_count;
                const total = reportedTotal ?? nextEnvelopes.length;
                writeListCache(cacheKey, nextEnvelopes, total);
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(total);
                const hasMore =
                    nextEnvelopes.length > 0 &&
                    (reportedTotal != null
                        ? nextEnvelopes.length < reportedTotal
                        : nextEnvelopes.length >= dataLength);
                setEnvelopesLeft(hasMore);
                setAllFetched(!hasMore);
                setEnvelopes(nextEnvelopes);
                setEnvelopesHistory(nextEnvelopes);
                loadMoreLockRef.current = false;
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
                loadMoreLockRef.current = false;
            }
        );
    }, [props.id, dataLength, sorting, searchItem, appliedFilter]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && envelopesLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;

            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchFormEnvos(
                props.id,
                nextPage,
                dataLength,
                sorting,
                appliedFilter,
                searchItem,
                (response: any) => {
                    const nextEnvelopes = response.data?.envelopes ?? [];
                    const reportedTotal = response.data?.total_count;
                    setIsLoading(false);
                    loadMoreLockRef.current = false;

                    setEnvelopes((prev) => {
                        const merged = [...prev, ...nextEnvelopes];
                        const total = reportedTotal ?? merged.length;
                        setTotalCount(total);
                        const hasMore =
                            nextEnvelopes.length > 0 &&
                            (reportedTotal != null
                                ? merged.length < reportedTotal
                                : nextEnvelopes.length >= dataLength);
                        setEnvelopesLeft(hasMore);
                        setAllFetched(!hasMore);
                        return merged;
                    });
                    setEnvelopesHistory((prev) => [
                        ...prev,
                        ...nextEnvelopes,
                    ]);
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                }
            );
        },
    });

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const reset = () => {
        invalidateListCacheByPage(`form-${props.id}-envelopes`);
        resetDataQueries?.();
        setAppliedFilter({});
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setLoadData(true);
        setIsLoading(true);
    };

    const handleApplyFilter = (filterOverride?: Record<string, any>) => {
        const nextFilter = filterOverride ?? filter;

        invalidateListCacheByPage(`form-${props.id}-envelopes`);
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setLoadData(true);
        setIsLoading(true);
        setAppliedFilter(nextFilter);
    };

    const envelopeIds = envelopes.map((envelope) => envelope.id).filter(Boolean);

    const reloadEnvelopes = () => {
        invalidateOwnershipTransferListCaches("envelope", { formId: props.id });
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setLoadData(true);
        setIsLoading(true);
        setAppliedFilter((prev: Record<string, any>) => ({ ...prev }));
    };

    const handleBulkTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || selection.selectedIds.length === 0) return;

        setBulkTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            const result = await transferBulkEnvelopeOwnership({
                envelope_ids: selection.selectedIds,
                ...payload,
            });
            selection.clear();
            setShowBulkTransferModal(false);
            successToasterMessage(
                result?.is_async
                    ? BULK_TRANSFER_OWNERSHIP_QUEUED
                    : BULK_TRANSFER_OWNERSHIP_SUCCESS,
            );
            reloadEnvelopes();
        } catch (error) {
            console.log("Error bulk transferring form envelope ownership:", error);
            errorToasterMessage(error);
        } finally {
            setBulkTransferLoading(false);
        }
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="form-envelope-table"
                    handleReset={reset}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    reset={reset}
                    search={search}
                    title="Envelopes"
                    options={StatusOptions}
                    handleApplyFilter={handleApplyFilter}
                    currentEnvo={envelopes.length}
                    totalEnvo={totalCount}
                    searchItem={searchItem as string}
                    selectedCount={showTransferActions ? selection.selectedIds.length : 0}
                    showMultiselectSelectAll={showTransferActions && isCompactLayout && envelopeIds.length > 0}
                    selectAllChecked={
                        selection.areAllSelected(envelopeIds)
                            ? true
                            : selection.areSomeSelected(envelopeIds)
                              ? "indeterminate"
                              : false
                    }
                    onSelectAllChange={() => selection.toggleAll(envelopeIds)}
                    multiselectAction={
                        showTransferActions ? (
                            <TransferOwnershipButton
                                id="form-envelope-table-bulk-transfer-btn"
                                iconId="form-envelope-table-bulk-transfer-icon"
                                className="gs-transfer-ownership-btn--toolbar"
                                onClick={() => setShowBulkTransferModal(true)}
                            />
                        ) : null
                    }
                />
            </div>
            <EnvelopeTable
                id="form-envelope-table"
                reset={reset}
                title="Envelopes"
                setEnvelopes={setEnvelopes}
                search={search}
                isLoading={isLoading}
                dataLength={dataLength as number}
                sorting={sorting}
                setSorting={setSorting as (sorting: any) => void}
                searchItem={searchItem as string}
                allFetched={allFetched}
                envelopes={envelopes}
                dataLoad={loadData}
                resetDataQueries={reloadEnvelopes}
                setPageNo={setPageNo}
                selection={selection}
                showTransferActions={showTransferActions}
            />
            {showBulkTransferModal ? (
                <TransferOwnershipModal
                    show={showBulkTransferModal}
                    onHide={() => setShowBulkTransferModal(false)}
                    onTransferOwnership={handleBulkTransferOwnership}
                    loading={bulkTransferLoading}
                    title={LABELS.TRANSFER_ENVELOPE_OWNERSHIP}
                    description={LABELS.BULK_TRANSFER_ENVELOPE_OWNERSHIP_DESCRIPTION}
                />
            ) : null}
        </div>
    );
};

export default FormEnvelopes;
